/**
 * The ticket sheet: one ticket, opened from a row, in the app's right sheet.
 *
 *   header   key · the stage button (opens "Where it has been") · More,
 *            Expand, Close · the title · the state pill, the agent, the
 *            repository, the pull request, and how long it has been like this
 *   body     the thing this state is about first (the questions when it needs
 *            you, the live stream when it does not), then context, folded
 *   footer   one sentence, one quiet secondary, one blue primary
 *            (`sheetFooterOf`); every other verb lives in More
 *
 * It is the case file re-composed: the data is the case file's (`useTicket`),
 * the blocks are the case file's (the plan reader, the live stream, the
 * attempt, the review card, the amendment cards, the split and back-to-Proposed
 * panels), and the chrome is the queue grammar. A Harper post, an Agent
 * request and a pull request review keep their own record bodies, under this
 * header, because those bodies own their verbs.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { PlanInputView, TicketQaCheck, WorkItem, WorkerSummary } from "@yagni/shared";
import {
  availableItemVerbs,
  canStartItemPlan,
  isLiveRun,
  isPausedRunAsk,
  isPublicationAsk,
  isStatedDecision,
  isStatedQuestion,
  PLAN_FOLLOW_UPS_SECTION,
  repoChosenByTeam,
  readJobGatePayload,
  resumesApprovedPlan,
  runUrl,
  skipChooses,
  stopOpenFindingPlace,
} from "@yagni/shared";
import { Link, useNavigate } from "react-router-dom";
import {
  Chip,
  Fold,
  MenuItem,
  MenuSeparator,
  Pill,
  RecordHead,
  SheetFoot,
  StagePie,
  Stamp,
  cx,
  type PillKind,
  type SheetVerb,
} from "../queue/index.js";
import { useSheet } from "../shell/useSheet.js";
import { askDockStore, useDockBinding } from "../shell/askDockStore.js";
import { ticketScope } from "../shell/yagniRailScopes.js";
import { primaryChipOf } from "./chatVerbs.js";
import {
  useAuthSession,
  useIsWorkspaceAdmin,
  useWorkspaceRole,
} from "../hooks/useWorkspaceRole.js";
import { Markdown } from "../components/Markdown.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import { PlanReader } from "../work/PlanReader.js";
import { ProposalReader, hasProposalSections } from "../work/ProposalReader.js";
import { LiveStream } from "../work/LiveStream.js";
import { AttemptBlock } from "../work/AttemptBlock.js";
import { laneSessionStartOf } from "../work/LaneProgress.js";
import { AmendmentCards } from "../work/AmendmentCards.js";
import { AskFacts, AskLink, AskNeed, AskPrLink, gateAskOf } from "../work/AskFacts.js";
import { originLine } from "../work/recordAtoms.js";
import { ReviewFindingsCards, PostedFindings } from "../work/ReviewFindings.js";
import { ReviewCard, ReviewCaseFile } from "../work/review/ReviewCaseFile.js";
import { usePullRequestReview, type PullRequestReview } from "../work/usePullRequestReview.js";
import {
  FletcherCheckCard,
  FletcherReceiptVideo,
  fletcherReceiptLine,
} from "../work/FletcherCheckCard.js";
import { useTicketQaCheck } from "../work/useTicketQaCheck.js";
import { ProctorStatus, proctorStatusLine, type ProctorStatusLine } from "../work/ProctorStatus.js";
import { useWorkItemRuns } from "../work/useWorkItemRuns.js";
import { useWorkItemById } from "./useQueueSheet.js";
import { adoptLiveRun } from "../work/workLifecycle.js";
import {
  driverOf,
  namesAnchor,
  rowStageItemOf,
  workingItemsOf,
  type PipelineRow,
} from "../work/workViews.js";
import { ReturnToProposedPanel, SplitPanel, type SplitDraftRow } from "../work/pageActs.js";
import { usePageActs } from "../work/usePageActs.js";
import { WorkItemPendingActionPanel } from "../tracker/WorkItemPendingActionPanel.js";
import { TicketComments } from "../work/TicketComments.js";
import { HarperJobCaseFile } from "../work/HarperJobCaseFile.js";
import { WorkerRequestCaseFile } from "../work/WorkerRequestCaseFile.js";
import {
  mergedByYagni,
  receiptCredits,
  receiptVerification,
  teamWithdrawalReason,
} from "../work/receipt.js";
import { trackerIssueLink, trackerIssueTitle } from "../work/caseBlocks.js";
import { stripSection } from "../work/planMarkdown.js";
import { PLAN_QUESTIONS_SECTION } from "../work/planQuestions.js";
import {
  REPO_LABEL,
  RepoSelect,
  patchWorkItemRepo,
  useTeamReposState,
  type TeamRepo,
} from "../work/repoPick.js";
import { Btn } from "../queue/primitives.js";
import { useWorkerLookup, type StageOwnerLookup } from "../work/useWorkerLookup.js";
import { useTeamIdentity } from "../work/useTeamIdentity.js";
import { deskAccent } from "../desks/deskAccent.js";
import {
  itemStoppedSentence,
  itemStopWords,
  openFindingsClause,
  PIPELINE_STAGE_CRAFT,
  PIPELINE_STAGE_LABEL,
  pipelineStageOf,
  rowStateOf,
  stageLabelOf,
  stagePieOf,
  stateSinceOf,
} from "../work/workStage.js";
import { STAGE_HUE } from "../ui/stagePie.js";
import { Skeleton } from "../ui/skeleton.js";
import { usePlanLineAuthority } from "../teams/usePlanLineAuthority.js";
import { captureUiError } from "../sentry.js";
import { toast } from "../ui/toast.js";
import { useTicket, type TicketState } from "./useTicket.js";
import {
  isReadyForPerson,
  laneAskOf,
  sheetFooterOf,
  type SheetFooterSpec,
  type SheetVerbId,
  whyStaysInline,
} from "./sheetFooter.js";
import { SETTLE_MS, useSheetActs } from "./useSheetActs.js";
import {
  AskCard,
  QuestionCards,
  RepoAskCard,
  sourceLabel,
  type QuestionToolActions,
} from "./QuestionCards.js";
import { PlanRepositoryLines } from "./PlanRepositoryLines.js";
import { PlanFollowUps } from "./PlanFollowUps.js";
import { StatedDecisions } from "./StatedDecisions.js";
import {
  readSplitOffer,
  SPLIT_OPTIONAL_LINE,
  splitIntoLabel,
  splitOfferFact,
} from "./splitOffer.js";
import { SplitChildrenFold, SplitPlaceBlock, wasSplit } from "./SplitChildren.js";
import { TicketEvidenceFold } from "./TicketEvidence.js";
import { readPersonOnlySteps } from "../work/personOnlySteps.js";
import { addTeamConnectedTool, fetchTeamReads, TeamWriteError } from "../teams/teamsApi.js";
import { Timeline } from "./Timeline.js";
import {
  absoluteTime,
  agoLabel,
  checkResultsOf,
  planningSpanOf,
  QUEUE_GROUP_LABEL,
  rowKeyOf,
  sheetGroupOf,
  shortAge,
  shortSpan,
  stageOwnerOf,
  ticketSentenceOf,
  type QueueGroup,
} from "./queueGroups.js";
import { fletcherNowLine, NowLines, proctorNowLine, type NowLine } from "./NowLines.js";
import { RunDecisionPanel, useOpenRunDecision } from "./RunDecision.js";
import { usePlanSelfReview } from "../work/usePlanSelfReview.js";
import { PlanSelfReview } from "../work/PlanSelfReview.js";
import {
  PrototypesStrip,
  useWorkItemPrototypes,
  type PrototypeDesignSnapshot,
} from "../work/PrototypesStrip.js";
import {
  EARLIER_RUN_LABEL,
  DISCARDED_NO_REASON,
  DISCARDED_PILL,
  DISCARDED_WHY_HEADING,
  discardedFacts,
  START_OVER_MENU,
  startOverToast,
  addNoteToast,
  viewPrLabel,
  gitHubStateChip,
  gitHubStateChipTone,
  pullRequestAskFacts,
  personOnlyStepLine,
  reeveWatchSetLine,
  attemptBeforeWorkLine,
  SEND_BACK_NOTE_EFFECT,
  SEND_BACK_NOTE_PLACEHOLDER,
} from "../work/workCopy.js";
import { StartOverDialog } from "./StartOverDialog.js";
import { AddNoteDialog } from "./AddNoteDialog.js";
import { approvedPlanAnswers } from "./planAnswerNotes.js";
import { PlanAnswersRecord } from "./PlanAnswersRecord.js";
import { PlanNoteLines, startOverReasonOf } from "./PlanAnswerLines.js";
import { PlanVersionDiff, PlanVersionLine } from "../work/PlanVersionLine.js";
import { DiscardedBefore } from "./DiscardedBefore.js";
import { NotMemberWhy, useAskToAddSource, useAskToJoin, useTeamPeopleFor } from "./notMember.js";
import "../work/work.css";
import "../work/caseFile.css";
import "../decisions/decisions.css";
import "./ticket.css";

export interface TicketSheetProps {
  item: WorkItem;
  /**
   * The row the board prints for this ticket (`pipelineRowFor`): the item plus
   * its Review and QA facets. The chip, the headline, "since" and This run
   * speak for the same item the row does (`driverOf`); gates, the footer and
   * the verbs stay the item's own, because that is what a person acts on.
   * Absent reads as the item alone.
   */
  row?: PipelineRow;
  /** The next ticket on the queue that needs the reader, for Next item. */
  nextItemId: string | null;
  onOpenItem: (id: string) => void;
  onClose: () => void;
}

/** Which record body a ticket gets: its own for a review case, a Harper post, an Agent request. */
export type BodyKind = "ticket" | "review" | "harper" | "request";

export function bodyKindOf(item: WorkItem): BodyKind {
  if (typeof item.sourceRef?.workerRequestId === "string" && item.sourceRef.workerRequestId)
    return "request";
  if (item.kind === "review") return "review";
  if (item.kind === "harper_job") return "harper";
  return "ticket";
}

/** Who carries a stage's craft here, by the same craft map the queue rows read. */
function stageWorkerName(
  workerOf: StageOwnerLookup,
  stage: "plan" | "review" | "build" | "qa",
  preferSlug: string | null | undefined,
): string | null {
  return stageWorker(workerOf, stage, preferSlug)?.displayName ?? null;
}

function stageWorker(
  workerOf: StageOwnerLookup,
  stage: "plan" | "review" | "build" | "qa",
  preferSlug: string | null | undefined,
): WorkerSummary | null {
  const craft = PIPELINE_STAGE_CRAFT[stage];
  return craft ? workerOf.byCraft(craft, preferSlug) : null;
}

/**
 * The pill names the row's group (`sheetGroupOf`), so the sheet and the
 * queue row can never disagree about where the ticket is (YAG-1079).
 */
const PILL: Record<QueueGroup, { kind: PillKind; label: string }> = {
  needs: { kind: "needs", label: QUEUE_GROUP_LABEL.needs },
  ready: { kind: "needs", label: QUEUE_GROUP_LABEL.ready },
  live: { kind: "live", label: QUEUE_GROUP_LABEL.live },
  stop: { kind: "stop", label: QUEUE_GROUP_LABEL.stop },
  done: { kind: "done", label: QUEUE_GROUP_LABEL.done },
};

function clock(iso: string | null | undefined): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const today = new Date();
  if (at.toDateString() === today.toDateString()) {
    return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * The head's time, said so it cannot read as how long a run took (GPX,
 * 2026-10-29): a live run's clock is "since 14:02 · running 22m", a plan at
 * its gate says how long it took to write and when it was ready ("planned
 * in 4m · 22m ago"), anything else is "since 14:02 · 22m ago". Empty parts
 * are left out; the moment itself is the tooltip.
 */
function headTimeOf(
  running: boolean,
  sinceIso: string | null | undefined,
  planSpan: number | null,
  now: number,
): string[] {
  const since = sinceIso ? `since ${clock(sinceIso)}` : "";
  if (running) return [since, sinceIso ? `running ${shortAge(sinceIso, now)}` : ""].filter(Boolean);
  const lead = planSpan != null ? `planned in ${shortSpan(planSpan)}` : since;
  return [lead, agoLabel(sinceIso, now)].filter(Boolean);
}

function AgentChip({ worker }: { worker: WorkerSummary | null }) {
  if (!worker) return null;
  return (
    <span className="chip agent" data-testid="ticket-agent">
      <WorkerMark avatar={worker.avatar} name={worker.displayName} slug={worker.slug} size="sm" />
      {worker.displayName}
    </span>
  );
}

/** The pull request's name in a prbox: a link to it on GitHub whenever the item knows where it is. */
function PrLabel({ item }: { item: WorkItem }) {
  const word = item.prNumber != null ? `PR #${item.prNumber}` : "Pull request";
  if (!item.prUrl) return <span className="pr">{word}</span>;
  return (
    <a
      className="pr"
      href={item.prUrl}
      target="_blank"
      rel="noreferrer"
      title="View pull request on GitHub"
    >
      {word}
    </a>
  );
}

function Blk({
  label,
  fact,
  mark,
  children,
}: {
  label: ReactNode;
  fact?: ReactNode;
  mark?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="blk">
      <div className="bh">
        {mark}
        <span className="label">{label}</span>
        {fact != null && <span className="fact">{fact}</span>}
      </div>
      {children}
    </div>
  );
}

function planMissingLine(loading: boolean): string {
  return loading ? "Reading the plan." : "The plan has no page yet.";
}

/** The plan as the reader shows it: its questions and follow-ups have their own blocks. */
function planBody(content: string | null | undefined): string {
  return content
    ? stripSection(stripSection(content, PLAN_QUESTIONS_SECTION), PLAN_FOLLOW_UPS_SECTION)
    : "";
}

/** Case, spacing and a closing full stop aside, the same line. */
function sameLine(a: string, b: string): boolean {
  const norm = (value: string) =>
    value.replace(/\s+/g, " ").replace(/[.:]$/, "").trim().toLowerCase();
  return norm(a) !== "" && norm(a) === norm(b);
}

/**
 * A proposal page opens by restating its title; the sheet head already says
 * it. A page that is only the title keeps it, so it never reads as empty.
 */
export function withoutRepeatedTitle(text: string, title: string): string {
  const body = text.trimStart();
  const firstBreak = body.search(/\n|$/);
  const first = body.slice(0, firstBreak).replace(/^#{1,6}\s*/, "");
  const rest = body.slice(firstBreak).trim();
  return sameLine(first, title) && rest ? rest : text;
}

/**
 * Bailey's own proposal page drops its restated title and lays out in sections
 * like the plan; anything else in this slot (a tracker spec, an assigned
 * ticket) is someone's markdown and prints as written, first line included.
 */
function ProposalBody({ text, title, isPage }: { text: string; title: string; isPage: boolean }) {
  if (!isPage) return <Markdown className="brief" text={text} />;
  const page = withoutRepeatedTitle(text, title);
  return hasProposalSections(page) ? (
    <ProposalReader markdown={page} />
  ) : (
    <Markdown className="brief" text={page} />
  );
}

export function TicketSheet(props: TicketSheetProps) {
  // Keyed on the ticket: every piece of per-ticket state (the answers being
  // typed, an open split panel) starts clean on the next one.
  return <TicketSheetBody key={props.item.id} {...props} />;
}

function TicketSheetBody({ item: listed, row, nextItemId, onOpenItem, onClose }: TicketSheetProps) {
  const session = useAuthSession();
  const workspaceRole = useWorkspaceRole();
  const viewerUserId = session?.user?.id ?? null;
  const workerOf = useWorkerLookup();
  const sheet = useSheet();
  const navigate = useNavigate();
  const listedFacet = row?.review ?? null;
  // What Proctor and Wright are doing on the pull request. It stays fresh on
  // its own while they work, and the merge facts re-read when it moves.
  const prReview = usePullRequestReview(
    listed.id,
    bodyKindOf(listed) === "ticket" && Boolean(listed.prUrl),
    listedFacet?.id ?? null,
  );
  // A deep link to an item the list does not carry has no facet folded in:
  // read the review case the pull request names, by id, and fold it only
  // when it names this item the way the board's fold does. It re-reads as Proctor moves.
  const caseRead = useWorkItemById(
    prReview.reviewCaseId,
    listedFacet == null && prReview.reviewCaseId != null,
    `${prReview.proctorStatus ?? ""}:${prReview.inProgress?.id ?? ""}:${prReview.report?.id ?? ""}`,
  );
  const reviewFacet =
    listedFacet ?? (caseRead && namesAnchor(caseRead, listed.id) ? caseRead : null);
  // Fletcher's latest check of the pull request (YAG-1077): its card beside
  // Proctor's review, and its line and video on the Receipt.
  const listedQa = row?.qa ?? null;
  const fletcher = useTicketQaCheck(
    listed.id,
    bodyKindOf(listed) === "ticket" && (Boolean(listed.prUrl) || listedQa != null),
    listedQa ? `${listedQa.id}:${listedQa.latestRunStatus ?? ""}:${listedQa.updatedAt}` : "",
  );
  const fletcherCard = fletcher.check ? (
    <FletcherCheckCard
      check={fletcher.check}
      workItemId={listed.id}
      onOpenCase={onOpenItem}
      onActed={fletcher.reread}
    />
  ) : null;
  const ticket = useTicket(
    listed,
    [
      prReview.proctorStatus ?? "",
      prReview.report?.id ?? "",
      prReview.fixRounds?.running ? "fixing" : "",
      prReview.fixRounds?.rounds ?? "",
    ].join(":"),
  );
  const item = ticket.liveItem;
  const { enrichmentOf, roster, ready: rosterReady } = useTeamIdentity();
  const deskName =
    (item.deskId ? roster.find((team) => team.id === item.deskId)?.name : null) ??
    item.teamName ??
    "";
  // Unknown when the roster does not list the Team (a read that failed or has
  // not landed): the verbs stay offered and the route answers a non-member 403.
  // Reading a Team the roster missed as "not a member" disabled Start plan on
  // the owner's own personal Team.
  const viewerIsTeamMember =
    rosterReady && item.deskId ? enrichmentOf(item.deskId)?.viewerIsMember : undefined;
  // Whether a fact card offers "Add Database to this Team" or asks the owner
  // to: the server's word, false until the roster says (the safe direction).
  const viewerCanManageTeam =
    rosterReady && item.deskId ? enrichmentOf(item.deskId)?.viewerCanManage === true : false;
  const { repos, failed: reposFailed, retry: rereadRepos } = useTeamReposState(item.deskId);
  // Whether Reeve approves his own plan on this Team, for every sentence that
  // would otherwise promise a sign-off.
  const planAuthority = usePlanLineAuthority(item.deskId);
  const repoName =
    repos
      ?.find((repo) => repo.id === item.repoId)
      ?.fullName.split("/")
      .pop() ?? null;
  const [repoBusy, setRepoBusy] = useState(false);
  const runDecision = useOpenRunDecision(
    useMemo(() => ticket.runs.map((run) => run.id), [ticket.runs]),
  );
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [answer, setAnswer] = useState("");
  const [note, setNote] = useState("");
  const [noteFor, setNoteFor] = useState<SheetVerbId | null>(null);
  const [panel, setPanel] = useState<"split" | "return" | null>(null);
  const [startingOver, setStartingOver] = useState(false);
  // Start over with an answer the plan cannot take: the dialog opens with it
  // as the reason, so Reeve's next plan reads it (spec 2026-11-02).
  const [startOverReason, setStartOverReason] = useState<string | null>(null);
  const startOverWith = useCallback((input: PlanInputView) => {
    setStartOverReason(startOverReasonOf(input));
    setStartingOver(true);
  }, []);
  const [addingNote, setAddingNote] = useState(false);
  // Stop ends the attempt, so it asks once before it fires.
  const [stopArmed, setStopArmed] = useState(false);
  const kind = bodyKindOf(item);

  // How the plan was checked before it reached a person, and the design
  // evidence it was drawn against: both read only while a plan is in play.
  const atPlanGate = ticket.gate?.kind === "approve_execution_plan";
  // The split a spanning plan's gate offers (YAG-1058), when it offers one.
  const splitOffer = readSplitOffer(ticket.gate);
  const gatePlanId =
    ticket.gate?.planId ??
    (typeof ticket.gate?.payload?.planId === "string" ? ticket.gate.payload.planId : null);
  const selfReviewData = usePlanSelfReview(
    item.id,
    atPlanGate ? gatePlanId : null,
    kind === "ticket" && (atPlanGate || item.phase === "planning"),
  );
  const selfReviewRun = selfReviewData?.planningRunId
    ? (ticket.runs.find((run) => run.id === selfReviewData.planningRunId) ?? null)
    : null;
  const selfReview = selfReviewData ? (
    <PlanSelfReview
      review={selfReviewData}
      agentName={workerOf(selfReviewRun?.personaSlug)?.displayName ?? null}
    />
  ) : null;
  const [prototypeRefresh, setPrototypeRefresh] = useState(0);
  const prototypeList = useWorkItemPrototypes(item.id, prototypeRefresh);
  const [designSnapshot, setDesignSnapshot] = useState<PrototypeDesignSnapshot | null>(null);
  const expectedPrototypeReferencesHash =
    designSnapshot && designSnapshot.planId === gatePlanId ? designSnapshot.hash : null;
  // The plan's UI preview stays on the work item for its whole journey: at
  // the gate it is the design being decided (and its hash rides the
  // approval), while Reeve plans it is on its way (the gate opens once it
  // lands), and afterwards it is the approved design the build used.
  const planningNow = item.phase === "planning";
  const planningSince = planningNow
    ? (ticket.runs.find((run) => isLiveRun(run) && run.purpose === "planning")?.startedAt ?? null)
    : null;
  const finished = item.phase === "done" || item.phase === "dismissed" || item.phase === "split";
  const prototypes =
    kind === "ticket" ? (
      <PrototypesStrip
        prototypes={prototypeList}
        workItemId={item.id}
        title={item.title}
        gatePlanId={atPlanGate ? gatePlanId : null}
        {...(atPlanGate ? { onDesignSnapshot: setDesignSnapshot } : {})}
        planState={atPlanGate || planningNow ? "planning" : "implementation"}
        allowCreate={atPlanGate}
        planning={planningNow}
        planningSince={planningSince}
        done={finished}
        onCreated={() => setPrototypeRefresh((value) => value + 1)}
      />
    ) : null;

  const liveRun = liveRunOf(ticket.runs, item.currentRunId);
  // The item as its own runs prove it (a fix round the list has not heard of
  // yet is live), folded with its facets the way the board folds it, so the
  // header and the row cannot disagree about who is speaking.
  const anchor = useMemo(() => adoptLiveRun(item, ticket.runs), [item, ticket.runs]);
  const sheetRow: PipelineRow = { anchor, review: reviewFacet, qa: row?.qa ?? null };
  const driver = driverOf(sheetRow);
  const facetDrives = driver !== anchor;
  // Who the live run is: the item's read of its latest run when that is this
  // run. The one-attempt lane has one run for the whole attempt, started as
  // Wright's, and its projection names Reeve while it plans.
  const liveSlug = liveRun
    ? ((liveRun.id === item.latestRunId ? item.latestRunPersonaSlug : null) ?? liveRun.personaSlug)
    : null;
  // The ticket's own Agent: never a check's, which is activity inside it.
  const worker =
    (liveSlug ? workerOf(liveSlug) : null) ??
    (item.currentGateAgentSlug ? workerOf(item.currentGateAgentSlug) : null) ??
    // Working with no run the roll-up can name yet: the stage's owner, as the
    // row names it, never the ticket's proposer.
    (rowStateOf(anchor) === "working" ? stageOwnerOf(anchor, workerOf) : null) ??
    workerOf(item.personaSlug) ??
    null;
  // The ticket's stage names the header, even while Fletcher tests its pull request.
  const stageItem = rowStageItemOf(sheetRow);
  const stage = pipelineStageOf(stageItem);
  const state = rowStateOf(driver);
  const group = sheetGroupOf(sheetRow, Date.now());
  const pill = PILL[group];
  const piePlain = stagePieOf(driver).mode;
  const pieMode = group === "needs" && piePlain === "past" ? "wait" : piePlain;
  const sentence = ticketSentenceOf(sheetRow, workerOf as StageOwnerLookup, viewerUserId);
  // Who the header names: everyone working on the ticket now (Proctor and
  // Fletcher both, while both are), else the checks whose results are in,
  // else the case speaking for the ticket (Proctor's held verdict), else the
  // ticket's own Agent (YAG-1079).
  const working = workingItemsOf(sheetRow);
  const results = checkResultsOf(sheetRow);
  const facetWorker = (facet: WorkItem | null, stageOfCheck: "review" | "qa") =>
    (facet ? workerOf(facet.personaSlug) : null) ??
    stageWorker(workerOf, stageOfCheck, facet?.personaSlug);
  const checkWorkers = working.some((each) => each !== anchor)
    ? working.map((each) =>
        each === anchor ? worker : facetWorker(each, each === sheetRow.qa ? "qa" : "review"),
      )
    : results
      ? [
          // Proctor's result is in when the row reads its verdict, or the
          // sheet holds its case or its posted review.
          results.proctor || reviewFacet || prReview.report
            ? facetWorker(reviewFacet, "review")
            : null,
          results.fletcher ? facetWorker(sheetRow.qa, "qa") : null,
        ]
      : facetDrives
        ? [facetWorker(driver, driver === sheetRow.qa ? "qa" : "review")]
        : [worker];
  const chips = checkWorkers.filter(
    (each, index, all): each is WorkerSummary =>
      each != null && all.findIndex((other) => other?.slug === each.slug) === index,
  );
  // A one-attempt lane run starts with its attempt, which can be hours before
  // the session now working (NEARM-523): working dates from that session.
  const sinceIso =
    (!facetDrives && state === "working" ? laneSessionStartOf(liveRun) : null) ??
    stateSinceOf(driver);
  // While a facet's run is live, that run is This run, and the build's own
  // newest run folds under it as the earlier one.
  const facetLive = facetDrives && state === "working";
  const facetRuns = useWorkItemRuns(driver.id, undefined, facetLive);
  const facetRun = facetLive ? (facetRuns.runs.find(isLiveRun) ?? null) : null;
  const reviewerName = stageWorkerName(workerOf, "review", reviewFacet?.personaSlug) ?? "Proctor";
  // A one-attempt lane fix round, from its session's own stage (YAG-1079).
  const laneFix =
    liveRun?.lane === "attempt" && liveRun.sessionStage === "fix"
      ? { asks: liveRun.fixAsks ?? null, round: liveRun.fixRound ?? 1 }
      : null;
  const proctorLine = proctorStatusLine(prReview, {
    prNumber: item.prNumber,
    reviewer: reviewerName,
    builder: stageWorkerName(workerOf, "build", item.personaSlug) ?? "Wright",
    attempt: reviewFacet?.activeRun?.attemptNo ?? null,
    pr: {
      openAsk: ticket.gate != null,
      draft: ticket.merge?.pullRequestDraft ?? item.pullRequest?.draft ?? null,
      reviewDecision:
        ticket.merge?.github?.reviewDecision ?? item.pullRequest?.reviewDecision ?? null,
      mergeableState:
        ticket.merge?.github?.mergeableState ?? item.pullRequest?.mergeableState ?? null,
    },
    laneFix,
  });
  // NOW: one line per check working on the pull request, Proctor's and
  // Fletcher's both while both are (YAG-1079). The ticket's own run is the
  // live stream, not a line here.
  const qaFacet = sheetRow.qa;
  const reviewWorking =
    proctorLine?.kind === "reviewing" ||
    (reviewFacet != null && rowStateOf(reviewFacet) === "working");
  const nowLines: NowLine[] =
    bodyKindOf(item) !== "ticket"
      ? []
      : [
          ...(reviewWorking
            ? [
                proctorNowLine({
                  worker: facetWorker(reviewFacet, "review"),
                  reviewer: reviewerName,
                  headSha: prReview.inProgress?.headSha ?? null,
                  prNumber: item.prNumber,
                  startedAt:
                    prReview.inProgress?.startedAt ?? reviewFacet?.activeRun?.startedAt ?? null,
                  attempt: reviewFacet?.activeRun?.attemptNo ?? null,
                  ...(reviewFacet ? { onWatch: () => onOpenItem(reviewFacet.id) } : {}),
                }),
              ]
            : []),
          ...(qaFacet && rowStateOf(qaFacet) === "working"
            ? [
                fletcherNowLine({
                  worker: facetWorker(qaFacet, "qa"),
                  tester: stageWorkerName(workerOf, "qa", qaFacet.personaSlug) ?? "Fletcher",
                  check: qaFacet,
                  prNumber: item.prNumber,
                  onWatch: () => onOpenItem(qaFacet.id),
                }),
              ]
            : []),
        ];

  // Connected tools added to the Team from a fact card this visit. The gate
  // keeps the question as it was asked, so the card says it was added and the
  // footer says how to use it: a new plan, by Send back.
  const [addedSources, setAddedSources] = useState<string[]>([]);
  const [addingSource, setAddingSource] = useState<string | null>(null);
  // Every workspace connection reaches the Team by default (YAG-986), so a
  // fact's missing source is hidden from the Team or not connected at all.
  // Read only when a question names one; null until it lands, which keeps
  // the card's older "add" shape rather than guessing.
  const needsToolSources = ticket.questions.some((question) => question.needsTool);
  const [hiddenSources, setHiddenSources] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    setHiddenSources(null);
    if (!item.deskId || !needsToolSources) return;
    fetchTeamReads(item.deskId)
      .then((reads) => {
        if (!cancelled)
          setHiddenSources(reads.connections.filter((c) => c.hidden).map((c) => c.source));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureUiError(err, {
          area: "Work",
          operation: "sheet_read_team_reads",
          deskId: item.deskId,
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [item.deskId, needsToolSources]);
  const viewerIsAdmin = useIsWorkspaceAdmin(false);
  const [addSourceFailed, setAddSourceFailed] = useState<{
    source: string;
    message: string;
  } | null>(null);
  const addSource = async (source: string) => {
    if (!item.deskId || addingSource) return;
    setAddingSource(source);
    setAddSourceFailed(null);
    try {
      await addTeamConnectedTool(item.deskId, source);
      setAddedSources((prev) => (prev.includes(source) ? prev : [...prev, source]));
      // The Team's own data follows: its roster row and its page.
      window.dispatchEvent(new CustomEvent("tickets.invalidate"));
    } catch (err) {
      captureUiError(err, {
        area: "Work",
        operation: "sheet_add_team_connected_tool",
        deskId: item.deskId,
        source,
      });
      setAddSourceFailed({
        source,
        message:
          err instanceof TeamWriteError
            ? err.message
            : `Could not add ${sourceLabel(source)}. Try again.`,
      });
    } finally {
      setAddingSource(null);
    }
  };
  // The questions only a person settles that are still open, and what Skip
  // can do about them: a fact has no recommendation, and a one-way decision
  // or a question with no recommendation is the person's to answer.
  const openAsked = ticket.askedQuestions.filter(
    (question) => !ticket.answeredIds.has(question.id),
  );
  const openFacts = openAsked.filter((question) => question.kind === "fact").length;
  const openNoSkip = openAsked.filter((question) => !skipChooses(question)).length;
  const reeveStated = ticket.statedQuestions.filter(
    (question) => !ticket.answeredIds.has(question.id),
  );
  const reeveDecisions = reeveStated.filter(isStatedDecision).length;
  const reeveFacts = reeveStated.length - reeveDecisions;
  const needsSource =
    !viewerCanManageTeam && ticket.questions.some((question) => question.needsTool);

  const gateAsk = gateAskOf(ticket.gate);
  const footer: SheetFooterSpec = sheetFooterOf({
    item,
    gate: ticket.gate,
    gateLoading: ticket.gateLoading,
    gateError: ticket.gateError != null,
    runs: ticket.runs,
    // What the plan does not hold yet holds Approve too (spec 2026-11-02):
    // an answer or note Reeve is writing in, and one that could not be. The
    // footer reads the Ask changes waiting off the gate.
    questions: {
      ...ticket.questionHold,
      applying: ticket.applyingCount,
      failed: ticket.failedCount,
    },
    openFacts,
    openNoSkip,
    reeveDecisions,
    reeveFacts,
    addedSources: addedSources.map(sourceLabel),
    answerReady: answer.trim().length > 0,
    merge: ticket.merge,
    wide: sheet.wide,
    hasNextItem: nextItemId != null,
    canStartPlan:
      !ticket.gate &&
      !ticket.gateLoading &&
      !ticket.runsLoading &&
      liveRun == null &&
      canStartItemPlan(
        item,
        ticket.runs.some(
          (run) => run.id === item.latestPlanningRunId || run.purpose === "planning",
        ),
      ),
    viewerIsTeamMember,
    workerName: worker?.displayName ?? null,
    planAuthority,
    runsLoading: ticket.runsLoading,
    canEditWorkspaceSettings: workspaceRole === "owner" || session?.user?.isSuperadmin === true,
  });

  const notMember = footer.kind === "not_member";
  // The Team's people: for the not-a-member footer, and for a fact card's
  // "Ask Priya to add Database to this Team".
  const { members: teamPeople, failed: teamPeopleFailed } = useTeamPeopleFor(
    item.deskId ?? null,
    notMember || needsSource,
  );
  const askToJoin = useAskToJoin({
    workItemId: item.id,
    teamName: deskName,
    members: teamPeople,
    memberVerb: footer.memberVerb ?? "answer this",
  });
  const askToAddSource = useAskToAddSource({
    workItemId: item.id,
    teamName: deskName,
    members: teamPeople,
    askerName: worker?.displayName ?? "Reeve",
    ...(hiddenSources ? { hiddenSources } : {}),
  });
  const toolActions: QuestionToolActions = {
    canManage: viewerCanManageTeam,
    ...(hiddenSources ? { hiddenSources } : {}),
    connectUrl: viewerIsAdmin ? "/services" : null,
    added: addedSources,
    adding: addingSource,
    failed: addSourceFailed,
    onAdd: (source) => void addSource(source),
    askLabel: askToAddSource.labelFor,
    askDisabled: askToAddSource.disabledFor,
    onAskOwner: (source, label) => void askToAddSource.ask(source, label),
  };
  const footWhyId = `sheet-why-${item.id}`;

  // The Ask dock follows the open ticket: its scope, its key in the bar, its
  // comment thread beside the conversation. Every Ask on the sheet claims it
  // first, so the chat that opens is this ticket's.
  const claimDock = useDockBinding({
    scope: ticketScope(
      item,
      ticket.gate?.kind === "approve_execution_plan",
      gateAsk != null,
      primaryChipOf(footer.primary, ticket.gate, item),
    ),
    context: rowKeyOf(item),
    comments: <TicketComments workItemId={item.id} />,
    onToolResult: (tool) => {
      if (tool === "yagni_item_act") {
        window.dispatchEvent(new Event("tickets.invalidate"));
        ticket.refreshAll();
      }
    },
  });

  const askAbout = useCallback(
    (text: string, planQuestion?: { id: string; text: string }) => {
      claimDock();
      askDockStore.prefill(text, planQuestion);
    },
    [claimDock],
  );
  const openDock = useCallback(() => {
    claimDock();
    askDockStore.setOpen(true);
  }, [claimDock]);

  const acts = useSheetActs({
    item,
    gate: ticket.gate,
    runs: ticket.runs,
    planVersionId: ticket.planVersion?.id ?? null,
    expectedPrototypeReferencesHash,
    answer,
    note,
    noteFor,
    merge: ticket.merge,
    review: ticket.review,
    onSettled: () => {
      setAnswer("");
      setNote("");
      setNoteFor(null);
      ticket.refreshAll();
    },
    onWide: () => sheet.setWide(true),
    onNext: () => (nextItemId ? onOpenItem(nextItemId) : onClose()),
    onOpenSuccessor: () => {
      const successor = item.discard?.successorWorkItemId;
      if (successor) onOpenItem(successor);
    },
    onStartOver: () => setStartingOver(true),
    onAddNote: () => setAddingNote(true),
    onNeedsNote: (verb) => setNoteFor(verb),
    onOpenPath: (path) => navigate(path),
    onRetryGate: () => void ticket.refetchGate(),
    claimDock,
    needsRepoChoice: !item.repoId && (repos?.length ?? 0) > 1,
  });

  // The Team has several repositories and the ticket names none. At the plan
  // gate the approval was refused: the pick is written to the ticket, then
  // the approval is sent again, the way the person meant it. Anywhere else the
  // accept was held before planning: writing the pick starts planning itself
  // (`ticketActs.ts`, held at `choose_repo`) and says how that went; a ticket
  // the PATCH does not start (it planned before) is started here.
  const chooseRepo = async (repoId: string) => {
    if (!repoId) return;
    setRepoBusy(true);
    try {
      const patched = await patchWorkItemRepo(item.id, repoId);
      if (!patched.ok) {
        captureUiError(new Error(patched.message), {
          area: "Work",
          operation: "sheet_choose_repo",
          workItemId: item.id,
          code: patched.code,
        });
        toast.error(patched.message);
        return;
      }
      acts.clearError();
      if (atPlanGate) {
        await acts.run("approve_plan");
      } else if (patched.planning === null && item.phase === "accepted" && !item.currentRunId) {
        // The PATCH reports a planning outcome only when it starts planning
        // itself (a ticket held at choose_repo that never planned). An
        // accepted ticket with no run that it did not start is started here.
        await acts.run("start_planning", { repoChosen: true });
      } else {
        if (patched.planning?.status === "not_started")
          acts.showHold(patched.planning.reason ?? "Planning has not started.");
        ticket.refreshAll();
      }
    } finally {
      setRepoBusy(false);
    }
  };
  const pageActs = usePageActs(item.id, ticket.refreshAll);

  /* ── the More menu: always the same list, filtered by what the ticket allows ── */
  const terminal = item.phase === "done" || item.phase === "dismissed" || item.phase === "split";
  const offered = availableItemVerbs({
    item: { ...item, stage, prUrl: item.prUrl, assigneeType: item.assigneeType },
    openGate: ticket.gate,
    planQuestions: ticket.questions.map((question) => ({
      id: question.id,
      answered: ticket.answeredIds.has(question.id),
      stated: isStatedQuestion(question),
    })),
    runs: ticket.runs,
    ...(viewerIsTeamMember === undefined ? {} : { viewerIsTeamMember }),
  });
  const canSplit = item.phase === "planning" || item.phase === "awaiting_execution_approval";
  const latestRun = ticket.runs[0] ?? null;
  const shareUrl =
    typeof window !== "undefined" && item.deskId
      ? `${window.location.origin}/desks/${encodeURIComponent(item.deskId)}?item=${encodeURIComponent(item.id)}`
      : "";
  const copy = (text: string, done: string) => {
    void navigator.clipboard?.writeText(text).then(
      () => toast.success(done),
      () => toast.error("Could not copy."),
    );
  };
  const closeTicket = async () => {
    if (!window.confirm("Close this ticket? Nothing more runs on it, and it leaves the queue."))
      return;
    try {
      const res = await fetch(`/api/work-items/${encodeURIComponent(item.id)}/dismiss`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Could not close the ticket (${res.status})`);
      window.dispatchEvent(new Event("tickets.invalidate"));
      ticket.refreshAll();
    } catch (err) {
      captureUiError(err, { area: "Work", operation: "sheet_close_ticket", workItemId: item.id });
      toast.error("Could not close the ticket. It is still on the queue.");
    }
  };
  const more = (
    <>
      {footer.more?.map((displaced) => (
        <MenuItem
          key={displaced.id}
          disabled={displaced.disabled === true || acts.pending != null || acts.settling}
          onSelect={() => void acts.run(displaced.id)}
        >
          {displaced.label}
        </MenuItem>
      ))}
      {canSplit && <MenuItem onSelect={() => setPanel("split")}>Split into tickets</MenuItem>}
      {offered.includes("return_to_proposed") && (
        <MenuItem onSelect={() => setPanel("return")}>Back to Proposed</MenuItem>
      )}
      {item.prUrl && (
        <MenuItem onSelect={() => window.open(item.prUrl!, "_blank", "noopener,noreferrer")}>
          {viewPrLabel(item.prNumber)} on GitHub
        </MenuItem>
      )}
      {latestRun && (
        <MenuItem onSelect={() => navigate(runUrl(item.id, latestRun.attemptNo))}>
          Open the full run page
        </MenuItem>
      )}
      {shareUrl && (
        <MenuItem onSelect={() => copy(shareUrl, "Copied the link.")}>Copy link</MenuItem>
      )}
      {!terminal && (
        <>
          <MenuSeparator />
          {offered.includes("start_over") && (
            <MenuItem onSelect={() => setStartingOver(true)}>{START_OVER_MENU}</MenuItem>
          )}
          <MenuItem onSelect={() => void closeTicket()}>Close this ticket</MenuItem>
        </>
      )}
    </>
  );
  const startOverStage: "plan" | "build" | "review" =
    item.phase === "final_review"
      ? "review"
      : item.phase === "executing" || item.phase === "needs_input"
        ? "build"
        : "plan";
  const sourceName =
    typeof item.sourceRef?.source === "string" ? item.sourceRef.source.toLowerCase() : "";
  const hasTracker =
    Boolean(item.trackerSource) || sourceName === "linear" || sourceName === "jira";
  const discardsBehind = kind === "ticket" ? (item.discardsBehind ?? []) : [];

  /* ── the body ── */
  const doc = ticket.doc;
  const planMarkdown = planBody(doc.planPage?.content);
  // Once the approval is past, the answers it recorded are said at the top of
  // the plan on the record.
  const approvedAnswers = atPlanGate
    ? null
    : approvedPlanAnswers(ticket.gateHistory, ticket.planRow);
  // What the plan found that the ticket did not ask for (ENG-2966), each a
  // ticket a person can make on this Team: read off the plan page, folded
  // under the plan (at the gate, under its body beside the other folds).
  const followUpsFold =
    kind === "ticket" ? (
      <PlanFollowUps
        pageContent={doc.planPage?.content}
        deskId={item.deskId ?? null}
        canCreate={viewerIsTeamMember !== false}
        onCreate={pageActs.createFollowUp}
        onOpen={onOpenItem}
      />
    ) : null;
  const planFold = planMarkdown ? (
    <Fold
      title="Plan it follows"
      count={
        ticket.planRow?.confirmedAt ? `approved ${clock(ticket.planRow.confirmedAt)}` : undefined
      }
    >
      {approvedAnswers && <PlanAnswersRecord record={approvedAnswers} />}
      <PlanReader markdown={planMarkdown} mode="record" />
      {followUpsFold}
    </Fold>
  ) : null;
  // A plan that spans repositories (YAG-1058): the gate says so under the
  // plan and offers the split, pre-filled with one ticket per repository in
  // build order. Approving the plan as-is keeps the one ticket, and the
  // offer says so. Its heading and its button say how the tickets build
  // from one helper (`splitTicketsFact`), so documentation built beside the
  // code reads the same in both.
  const splitBeside = splitOffer?.repositories.filter((repo) => repo.beside).length ?? 0;
  const splitOfferBlock =
    atPlanGate && splitOffer && panel !== "split" ? (
      <Blk label={splitOffer.line} fact={splitOfferFact(splitOffer)}>
        <ul className="md-body brief" data-testid="ticket-split-offer">
          {splitOffer.repositories.map((repo) => (
            <li key={repo.repoId}>
              <strong>{repo.order}.</strong> <code>{repo.fullName}</code>
              {repo.summary ? <> · {repo.summary}</> : null}
            </li>
          ))}
        </ul>
        <p className="body-p" data-testid="ticket-split-offer-optional">
          {SPLIT_OPTIONAL_LINE}
        </p>
        <div className="tri-actions">
          <button
            type="button"
            className="is-quiet"
            data-testid="ticket-split-offer-open"
            onClick={() => setPanel("split")}
          >
            {splitIntoLabel(splitOffer.children.length, splitBeside)}
          </button>
        </div>
      </Blk>
    ) : null;
  const buildAttempt =
    latestRun && ticket.runView ? (
      <AttemptBlock
        workItemId={item.id}
        run={latestRun}
        runs={ticket.runs}
        view={ticket.runView}
        viewFailed={ticket.runViewFailed}
        live={liveRun != null}
        workerName={worker?.displayName ?? null}
        prNumber={item.prNumber}
        prUrl={item.prUrl}
        {...(facetRun ? { label: EARLIER_RUN_LABEL } : {})}
      />
    ) : null;
  const attemptFold = facetRun ? (
    <>
      <AttemptBlock
        workItemId={driver.id}
        run={facetRun}
        runs={facetRuns.runs}
        view={ticket.runView}
        live
        workerName={
          facetWorker(driver, driver === sheetRow.qa ? "qa" : "review")?.displayName ?? null
        }
        prNumber={item.prNumber}
        prUrl={item.prUrl}
      />
      {buildAttempt}
    </>
  ) : (
    buildAttempt
  );
  // Where the plan says the work belongs (ENG-2966, ENG-3033): a move to
  // another of the Team's repositories, and what the Team lacks.
  const repositoryLines = atPlanGate ? (
    <PlanRepositoryLines
      gate={ticket.gate}
      canMove={viewerIsTeamMember !== false}
      onMove={async (move) => {
        const moved = await pageActs.startOver({
          reason: `Moved to ${move.fullName}${move.why ? `: ${move.why}` : "."}`,
          closePullRequest: false,
          deleteBranch: false,
          moveTo: { repoId: move.repoId, why: move.why },
        });
        onOpenItem(moved.successorWorkItemId);
      }}
    />
  ) : null;
  const panels = (
    <>
      {repositoryLines}
      {splitOfferBlock}
      {panel === "split" && (
        <SplitPanel
          suggested={
            splitOffer
              ? splitOffer.children
              : ticket.questions.map(
                  (question): SplitDraftRow => ({ title: question.text, steps: "" }),
                )
          }
          repos={repos ?? []}
          onCancel={() => setPanel(null)}
          onSplit={async (children, idempotencyKey) => {
            await pageActs.split(children, idempotencyKey);
            setPanel(null);
            onClose();
          }}
        />
      )}
      {panel === "return" && (
        <ReturnToProposedPanel
          split={item.phase === "split"}
          onCancel={() => setPanel(null)}
          onReturn={async (text) => {
            await pageActs.returnToProposed(text);
            setPanel(null);
          }}
        />
      )}
    </>
  );

  let body: ReactNode;
  let showFooter = true;
  if (kind !== "ticket") {
    showFooter = false;
    const caseProps = {
      item,
      deskName,
      accent: deskAccent(item.deskId ?? ""),
      workerOf,
      onNext: () => (nextItemId ? onOpenItem(nextItemId) : onClose()),
      onAskYagni: (text: string) => askAbout(text),
    };
    if (kind === "review") body = <ReviewCaseFile {...caseProps} context="dialog" />;
    else if (kind === "harper") body = <HarperJobCaseFile {...caseProps} />;
    else body = <WorkerRequestCaseFile {...caseProps} />;
  } else if ((!ticket.gate || isPublicationAsk(ticket.gate)) && ticket.pendingAction && !terminal) {
    // A refused publication's ask is answered on the publication card.
    showFooter = false;
    body = (
      <WorkItemPendingActionPanel
        open={ticket.pendingAction}
        onResolved={ticket.refreshAll}
        deskId={item.deskId}
      />
    );
  } else if (!ticket.gate && !ticket.gateLoading && runDecision.decision && !terminal) {
    showFooter = false;
    body = (
      <RunDecisionPanel
        decision={runDecision.decision}
        onResolved={() => {
          runDecision.reload();
          ticket.refreshAll();
        }}
      />
    );
  } else {
    body = (
      <StateBody
        footer={footer}
        item={item}
        ticket={ticket}
        prReview={prReview}
        proctorLine={proctorLine}
        worker={worker}
        answer={answer}
        setAnswer={setAnswer}
        askAbout={askAbout}
        planFold={planFold}
        followUpsFold={followUpsFold}
        attemptFold={attemptFold}
        planMarkdown={planMarkdown}
        sentence={sentence}
        selfReview={selfReview}
        prototypes={prototypes}
        toolActions={toolActions}
        onOpenItem={onOpenItem}
        onStartOverWith={startOverWith}
        proctorInNow={reviewWorking}
        laneSpeakers={{
          plan: stageWorkerName(workerOf, "plan", null) ?? "Reeve",
          build: stageWorkerName(workerOf, "build", item.personaSlug) ?? "Wright",
        }}
        // Fletcher's check of the pull request (YAG-1077), beside Proctor's
        // review wherever the body shows the review, and on the Receipt.
        fletcherCheck={fletcherCard}
        qaCheck={fletcher.check}
      />
    );
  }

  const askToJoinVerb: SheetVerb = {
    label: askToJoin.label,
    disabled: askToJoin.disabled,
    onClick: () => void askToJoin.ask(),
  };
  const secondaryVerb = footer.secondary;
  const footSecondary: SheetVerb | undefined = secondaryVerb
    ? {
        label: stopArmed && secondaryVerb.id === "stop_run" ? "Stop the run" : secondaryVerb.label,
        disabled: secondaryVerb.disabled === true || acts.pending != null || acts.settling,
        onClick: () => {
          if (secondaryVerb.id === "stop_run" && !stopArmed) {
            setStopArmed(true);
            return;
          }
          setStopArmed(false);
          void acts.run(secondaryVerb.id);
        },
      }
    : undefined;

  const planSpan = atPlanGate
    ? planningSpanOf(ticket.runs, ticket.gate?.openedAt ?? sinceIso)
    : null;
  const sinceLine = headTimeOf(state === "working", sinceIso, planSpan, Date.now()).join(" · ");
  // The assignment as the reason, on the record's own head: "assigned by Jared in Jira".
  const assignedLine = item.sourceRef?.ingestedVia === "assignment" ? originLine(item) : null;
  // The record's key is the link to its issue in Linear or Jira, whatever the
  // stage; a key with no issue page stays plain text.
  const trackerLink = trackerIssueLink(item);
  const recordKey =
    trackerLink && trackerLink.label !== "Issue" ? (
      <a
        href={trackerLink.url}
        target="_blank"
        rel="noreferrer"
        title={trackerIssueTitle(trackerLink)}
        data-testid="ticket-tracker-link"
      >
        {trackerLink.label}
      </a>
    ) : (
      rowKeyOf(item)
    );

  return (
    <>
      <RecordHead
        recordKey={recordKey}
        kick={
          <button
            type="button"
            className="stagebtn"
            style={{ "--stage": STAGE_HUE[stage] } as CSSProperties}
            aria-expanded={timelineOpen}
            onClick={() => setTimelineOpen((open) => !open)}
            data-testid="ticket-stage"
          >
            <StagePie stage={stage} mode={pieMode} size={18} />
            {stageLabelOf(stageItem)}
          </button>
        }
        more={more}
        title={item.title}
        onAsk={openDock}
        onClose={onClose}
        meta={
          <>
            <Pill kind={pill.kind} dot={group !== "done"}>
              {item.phase === "dismissed" ? (item.discard ? DISCARDED_PILL : "Closed") : pill.label}
            </Pill>
            {chips.map((chip) => (
              <AgentChip key={chip.slug} worker={chip} />
            ))}
            {repoName && (
              <Chip
                title={
                  repoChosenByTeam(item.sourceRef, item.repoId)
                    ? "The Team chose this repository; nobody has confirmed it"
                    : undefined
                }
              >
                {repoName}
                {repoChosenByTeam(item.sourceRef, item.repoId) ? " · best estimate" : null}
              </Chip>
            )}
            {item.prNumber != null && (
              <Chip
                href={item.prUrl ?? undefined}
                title={item.prUrl ? "View pull request on GitHub" : undefined}
              >
                PR #{item.prNumber}
              </Chip>
            )}
            {assignedLine && <Chip>{assignedLine}</Chip>}
            {sinceLine && (
              <span
                className="fact"
                style={{ marginLeft: "auto", fontSize: 11.5 }}
                title={absoluteTime(sinceIso) || undefined}
                data-testid="ticket-since"
              >
                {sinceLine}
              </span>
            )}
          </>
        }
      />
      <div
        className={cx("sbody ticket-body", acts.settling && "settling")}
        style={
          acts.settling
            ? { opacity: 0.35, transition: `opacity ${SETTLE_MS}ms var(--settle-ease)` }
            : undefined
        }
        data-testid="ticket-body"
      >
        {timelineOpen && (
          <Timeline
            item={item}
            activity={ticket.activity}
            gates={ticket.gateHistory}
            nowSentence={sentence}
            nowSince={clock(sinceIso)}
          />
        )}
        <NowLines lines={nowLines} />
        {kind === "ticket" && <SplitPlaceBlock item={item} onOpen={onOpenItem} />}
        {body}
        <DiscardedBefore discards={discardsBehind} onOpen={(id) => onOpenItem(id)} />
        {panels}
        {(noteFor || (showFooter && footer.note)) && (
          <div className="blk" data-testid="sheet-note">
            <textarea
              className="wk-gate-input"
              autoFocus={noteFor !== null}
              rows={3}
              value={note}
              placeholder={
                noteFor === "send_back"
                  ? SEND_BACK_NOTE_PLACEHOLDER
                  : noteFor
                    ? "What should the next pass change?"
                    : footer.note?.placeholder
              }
              aria-label="Your note"
              onChange={(event) => setNote(event.target.value)}
            />
            {noteFor === "send_back" && (
              <p className="wk-gate-note" data-testid="sheet-note-effect">
                {SEND_BACK_NOTE_EFFECT}
              </p>
            )}
          </div>
        )}
        {(acts.errorCode === "choose_repo" || footer.kind === "choose_repo") && (
          <RepoChoiceBlock
            repos={repos}
            failed={reposFailed}
            value={item.repoId ?? ""}
            disabled={repoBusy || acts.pending != null}
            onChoose={(repoId) => void chooseRepo(repoId)}
            onRetry={rereadRepos}
          />
        )}
        {acts.error && !showFooter && (
          <p className="wk-gate-err" role="alert">
            {acts.error}
          </p>
        )}
      </div>
      {showFooter && (
        <SheetFoot
          error={acts.error}
          whyId={footWhyId}
          // The sentence goes behind the "i" unless the person needs it to
          // act: who can act (not a member), the Stop confirmation, or why the
          // primary cannot be pressed.
          whyAs={notMember || stopArmed || whyStaysInline(footer) ? "inline" : "info"}
          why={
            notMember ? (
              <NotMemberWhy
                teamName={deskName}
                members={teamPeople}
                failed={teamPeopleFailed}
                memberVerb={footer.memberVerb ?? "answer this"}
              />
            ) : (
              <>
                {footer.label && footer.kind === "generic" && <b>{footer.label} · </b>}
                {stopArmed && footer.secondary?.id === "stop_run"
                  ? "Stopping ends this run. Anything it has not committed is lost, and the ticket comes back to you."
                  : footer.why}
                {footer.link ? (
                  <>
                    {" "}
                    {footer.link.lead} <Link to={footer.link.href}>{footer.link.label}</Link>.
                  </>
                ) : null}
                {footer.bold && !(stopArmed && footer.secondary?.id === "stop_run") ? (
                  <>
                    {" "}
                    <b>{footer.bold}</b>
                  </>
                ) : null}
              </>
            )
          }
          ask={
            !notMember && footer.ask
              ? {
                  label: footer.ask.label,
                  disabled: acts.pending != null || acts.settling,
                  onClick: () => void acts.run(footer.ask!.id),
                }
              : undefined
          }
          secondary={notMember ? askToJoinVerb : footSecondary}
          primary={
            footer.primary
              ? {
                  label:
                    acts.pending === footer.primary.id
                      ? `${footer.primary.label}…`
                      : footer.primary.label,
                  disabled:
                    footer.primary.disabled === true || acts.pending != null || acts.settling,
                  onClick: () => void acts.run(footer.primary!.id),
                  ...(footer.primary.disabled ? { describedBy: footWhyId } : {}),
                }
              : undefined
          }
        />
      )}
      <StartOverDialog
        open={startingOver}
        onClose={() => {
          setStartingOver(false);
          setStartOverReason(null);
        }}
        ticketKey={rowKeyOf(item)}
        stage={startOverStage}
        prNumber={item.prUrl ? item.prNumber : null}
        branchName={item.branchName}
        hasTracker={hasTracker}
        {...(startOverReason ? { initialReason: startOverReason } : {})}
        onConfirm={async (choice) => {
          const result = await pageActs.startOver(choice);
          setStartingOver(false);
          setStartOverReason(null);
          if (result.planning === "not_started") toast.warning(startOverToast(result.planning));
          else toast.success(startOverToast(result.planning));
          window.dispatchEvent(new Event("tickets.invalidate"));
          onOpenItem(result.successorWorkItemId);
        }}
      />
      <AddNoteDialog
        open={addingNote}
        onClose={() => setAddingNote(false)}
        ticketKey={rowKeyOf(item)}
        onConfirm={async (body) => {
          const result = await pageActs.leaveNextStepNote(body);
          setAddingNote(false);
          toast.success(addNoteToast(rowKeyOf(item), result.stepComing));
        }}
      />
    </>
  );
}

/**
 * The repository choice an approval was refused for: the Team's repositories
 * as a select, or the state of reading them, so the refusal in the footer
 * always has something to act on above it.
 */
function RepoChoiceBlock({
  repos,
  failed,
  value,
  disabled,
  onChoose,
  onRetry,
}: {
  repos: TeamRepo[] | null;
  failed: boolean;
  value: string;
  disabled: boolean;
  onChoose: (repoId: string) => void;
  onRetry: () => void;
}) {
  return (
    <div className="blk" data-testid="sheet-repo-choice">
      <div className="bh">
        <span className="label">Builds in</span>
      </div>
      {failed ? (
        <span className="fact" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          The Team's repositories could not be read.
          <Btn variant="quiet" size="sm" onClick={onRetry}>
            Try again
          </Btn>
        </span>
      ) : repos === null ? (
        <span className="fact">Reading the Team's repositories.</span>
      ) : repos.length === 0 ? (
        <span className="fact">
          The Team has no repository the workspace has connected. Attach one in Team settings.
        </span>
      ) : (
        <RepoSelect
          repos={repos}
          value={value}
          disabled={disabled}
          label={REPO_LABEL}
          testId="sheet-repo"
          onChange={onChoose}
        />
      )}
    </div>
  );
}

/** The body for one footer state, from the case file's own blocks. */
/**
 * The run the sheet watches: the one the item says is live when the roll-up
 * holds it, else the roll-up's live one. A Bailey reading pass and the
 * attempt's run can both read as live; the item's pointer is the tie-break.
 */
function liveRunOf<R extends { id: string; status: string }>(
  runs: readonly R[],
  currentRunId: string | null | undefined,
): R | null {
  return (
    (currentRunId ? runs.find((run) => run.id === currentRunId && isLiveRun(run)) : undefined) ??
    runs.find(isLiveRun) ??
    null
  );
}

function StateBody({
  footer,
  item,
  ticket,
  prReview,
  proctorLine,
  worker,
  answer,
  setAnswer,
  askAbout,
  planFold,
  followUpsFold,
  attemptFold,
  planMarkdown,
  sentence,
  selfReview,
  prototypes,
  toolActions,
  onOpenItem,
  onStartOverWith,
  proctorInNow,
  laneSpeakers,
  fletcherCheck,
  qaCheck,
}: {
  selfReview: ReactNode;
  prototypes: ReactNode;
  toolActions: QuestionToolActions;
  onOpenItem: (id: string) => void;
  /** Start over with an answer or a note the plan cannot take, as the reason. */
  onStartOverWith: (input: PlanInputView) => void;
  /** Proctor's review in flight is a line in the Now block above, so its status line is not said twice. */
  proctorInNow: boolean;
  /** Who speaks in a lane run's plan session and in its other sessions. */
  laneSpeakers: { plan: string | null; build: string | null };
  /** Fletcher's check card, shown beside Proctor's review wherever the body shows it. */
  fletcherCheck: ReactNode;
  /** Fletcher's last check itself, for the Receipt's line and video. */
  qaCheck: TicketQaCheck | null;
  footer: SheetFooterSpec;
  item: WorkItem;
  ticket: TicketState;
  prReview: PullRequestReview;
  proctorLine: ProctorStatusLine | null;
  worker: WorkerSummary | null;
  answer: string;
  setAnswer: (text: string) => void;
  askAbout: (text: string, planQuestion?: { id: string; text: string }) => void;
  planFold: ReactNode;
  /** The plan's follow-ups, folded under its body at the gate (ENG-2966). */
  followUpsFold: ReactNode;
  attemptFold: ReactNode;
  planMarkdown: string;
  sentence: string;
}) {
  const key = rowKeyOf(item);
  const gate = ticket.gate;
  const gateAsk = gateAskOf(gate);
  // What changed in the plan's version, opened from its line on the Plan block.
  const [whatChanged, setWhatChanged] = useState(false);
  // Wright's ask about its pull request also says what the pull request
  // itself shows: a merge Wright pushed, a conflict with the base.
  const prAskFacts =
    gate && readJobGatePayload(gate.payload)?.act === "push_fix"
      ? pullRequestAskFacts(ticket.merge)
      : [];
  const personOnlySteps =
    gate?.kind === "approve_execution_plan" ? readPersonOnlySteps(gate.payload) : [];
  const workerName = worker?.displayName ?? null;
  const linkedIssue = trackerIssueLink(item);
  const doc = ticket.doc;
  const { report: postedReview, deliveredFindings, followUp, threads } = prReview;
  // Proctor's review as the pull request holds it: every thread when the
  // record has them, else the newest review's findings. While Proctor or
  // Wright is at work, the last posted review folds under what they are doing.
  const reviewCard = postedReview ? (
    <>
      <ReviewCard
        report={postedReview}
        followUp={followUp}
        hasOpenGate={false}
        hideLabel
        findingsShownBelow={threads.length > 0 || deliveredFindings.length > 0}
      />
      <PostedFindings
        findings={deliveredFindings}
        threads={threads}
        dropped={postedReview.refinementDecisions?.dropped ?? null}
      />
    </>
  ) : null;
  const moving =
    proctorLine?.kind === "queued" ||
    proctorLine?.kind === "reviewing" ||
    proctorLine?.kind === "fixing";
  const proctorReview = reviewCard ? (
    moving ? (
      <div className="span">
        <Fold title="Proctor's last review">{reviewCard}</Fold>
      </div>
    ) : (
      <Blk label="Proctor's review">{reviewCard}</Blk>
    )
  ) : null;
  // Proctor's review and Fletcher's check, side by side: the two checks on the
  // pull request (YAG-1079).
  const checkCards =
    proctorReview || fletcherCheck ? (
      <>
        {proctorReview}
        {fletcherCheck}
      </>
    ) : null;
  // The pull request is in Review: what Proctor and Wright are doing about it
  // speaks in every body, not only where it waits on a merge. A review in
  // flight is already a line in the Now block.
  const inReview = Boolean(item.prUrl) && pipelineStageOf(item) === "review";
  const proctorStatus =
    inReview && !(proctorInNow && proctorLine?.kind === "reviewing") ? (
      <ProctorStatus line={proctorLine} />
    ) : null;
  // What Wright's own review left open when the build finished, as the pull
  // request names it (YAG-1106): a round cap opens the pull request rather
  // than stopping the ticket, so the sheet says what the reviewer will find.
  const leftOpen = openFindingsClause(item);
  const leftForReview =
    inReview && leftOpen ? (
      <Blk label="Left for review" fact={leftOpen}>
        <div data-testid="ticket-left-for-review">
          <StopOpenFindings item={item} />
        </div>
      </Blk>
    ) : null;

  const proposalText = doc.role === "proposal" ? (doc.page?.content ?? "") : item.description;
  const proposalFold =
    doc.role !== "proposal" && item.description ? (
      <Fold title="Spec it was planned from">
        <Markdown className="wk-art-note" text={item.description} />
      </Fold>
    ) : null;
  // What the spec read live, and what it could not (YAG-1005): beside the
  // spec wherever the spec is shown, open while the spec is the work. Read
  // once per ticket, and only for one a tracker backs.
  const evidenceFold = <TicketEvidenceFold item={item} />;

  // What the ticket is doing is still being read (no gate, no runs yet): a
  // neutral block, never a state the next read takes back.
  if (footer.kind === "loading" && !gate && ticket.runsLoading && ticket.runs.length === 0) {
    return (
      <div className="span" data-testid="ticket-body-loading" aria-busy="true">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="mt-2 h-24 w-full" />
      </div>
    );
  }

  switch (footer.kind) {
    case "proposed":
      return (
        <>
          <Blk
            label={
              item.sourceRef?.ingestedVia === "assignment"
                ? "Assigned to yagni"
                : item.sourceRef?.trackerSpec
                  ? "From your tracker"
                  : "Bailey proposes"
            }
            mark={
              worker ? (
                <WorkerMark
                  avatar={worker.avatar}
                  name={worker.displayName}
                  slug={worker.slug}
                  size="sm"
                />
              ) : null
            }
          >
            {proposalText ? (
              <ProposalBody
                text={proposalText}
                title={item.title}
                isPage={doc.role === "proposal"}
              />
            ) : (
              <p className="body-p">The proposal has no write-up yet.</p>
            )}
          </Blk>
          {gateAsk && (
            <Blk label="Why it is here">
              <AskFacts facts={gateAsk.facts} limit={4} />
            </Blk>
          )}
          <div className="span">
            <TicketEvidenceFold item={item} defaultOpen />
            {doc.role === "proposal" && item.description && (
              <Fold title={`Ticket as written${linkedIssue ? ` in ${linkedIssue.label}` : ""}`}>
                <Markdown className="wk-art-note" text={item.description} />
              </Fold>
            )}
          </div>
        </>
      );
    case "plan": {
      const counts = ticket.planRow
        ? [
            Array.isArray(ticket.planRow.steps) ? `${ticket.planRow.steps.length} steps` : null,
            Array.isArray(ticket.planRow.filesToChange)
              ? `${ticket.planRow.filesToChange.length} files`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : null;
      const asker = workerName ?? "Reeve";
      return (
        <>
          <StatedDecisions
            questions={ticket.statedQuestions}
            answers={ticket.answers}
            onAnswer={ticket.answerQuestion}
            onUndo={ticket.undoAnswer}
            onRetry={ticket.retryInput}
            onStartOver={onStartOverWith}
            onKeepAsDecision={ticket.keepAsDecision}
            onAsk={(question) =>
              askAbout(`On ${key}, a question about this: ${question.text}`, {
                id: question.id,
                text: question.text,
              })
            }
            askerName={asker}
          />
          {ticket.askedQuestions.length > 0 && (
            <QuestionCards
              questions={ticket.askedQuestions}
              answers={ticket.answers}
              onAnswer={ticket.answerQuestion}
              onUndo={ticket.undoAnswer}
              onRetry={ticket.retryInput}
              onStartOver={onStartOverWith}
              onKeepAsDecision={ticket.keepAsDecision}
              onAsk={(question) =>
                askAbout(`On ${key}, help me decide: ${question.text}`, {
                  id: question.id,
                  text: question.text,
                })
              }
              askerName={asker}
              // Reeve writes the plan, so its dot wears the plan stage's hue.
              agentColor={STAGE_HUE.plan}
              toolActions={toolActions}
            />
          )}
          <PlanNoteLines
            inputs={ticket.planInputs}
            askerName={asker}
            onRetry={ticket.retryInput}
            onDismiss={ticket.dismissInput}
            onStartOver={onStartOverWith}
          />
          {ticket.answerError && (
            <p className="wk-gate-err" role="alert" data-testid="plan-answer-error">
              {ticket.answerError}
            </p>
          )}
          {gate && (
            <AmendmentCards
              workItemId={item.id}
              gate={gate}
              planPageId={doc.planPage?.id ?? null}
              onChanged={ticket.refreshAll}
            />
          )}
          <Blk
            label="Plan"
            fact={
              ticket.planVersion ? (
                <PlanVersionLine
                  version={ticket.planVersion}
                  counts={counts}
                  open={whatChanged}
                  onToggle={() => setWhatChanged((open) => !open)}
                />
              ) : (
                (counts ?? undefined)
              )
            }
          >
            {whatChanged && ticket.planVersion && (
              <PlanVersionDiff workItemId={item.id} version={ticket.planVersion} />
            )}
            {planMarkdown ? (
              <PlanReader
                markdown={planMarkdown}
                questionCount={ticket.questions.length}
                mode="gate"
              />
            ) : (
              <p className="body-p">{planMissingLine(doc.loading)}</p>
            )}
          </Blk>
          <div className="span">
            {planMarkdown ? followUpsFold : null}
            {Array.isArray(ticket.planRow?.filesToChange) &&
              ticket.planRow!.filesToChange!.length > 0 && (
                <Fold title="Files it will touch" count={ticket.planRow!.filesToChange!.length}>
                  <div className="files">
                    {ticket.planRow!.filesToChange!.map((file, index) => {
                      const path =
                        typeof file === "string" ? file : ((file as { path?: string }).path ?? "");
                      const change =
                        typeof file === "string"
                          ? ""
                          : ((file as { change?: string }).change ?? "");
                      return (
                        <div key={`${path}-${index}`}>
                          <span>{path}</span>
                          <span>{change}</span>
                        </div>
                      );
                    })}
                  </div>
                </Fold>
              )}
            {(ticket.reeveRead || selfReview || ticket.planChecks.length > 0) && (
              <Fold
                title="Checks Reeve ran"
                count={
                  // Both kinds of check the fold lists: the facts read and the
                  // concerns weighed.
                  ticket.reeveRead?.watchSet || ticket.planChecks.length > 0
                    ? (ticket.reeveRead?.watchSet?.weighed ?? 0) + ticket.planChecks.length
                    : undefined
                }
              >
                {/* The facts Reeve settled by reading with the Team's
                    connected tools: not questions, so they are said here. */}
                {ticket.planChecks.length > 0 && (
                  <div className="checks" data-testid="plan-checks">
                    {ticket.planChecks.map((check, index) => (
                      <div key={`${check.tool}-${index}`}>
                        <span className="ok">Checked</span>
                        <span>{check.text}</span>
                      </div>
                    ))}
                  </div>
                )}
                {ticket.reeveRead?.watchSet && (
                  <p>
                    {reeveWatchSetLine(
                      ticket.reeveRead.watchSet.weighed,
                      ticket.reeveRead.watchSet.raised,
                    )}
                  </p>
                )}
                {ticket.reeveRead?.rationale && (
                  <Markdown className="wk-art-note" text={ticket.reeveRead.rationale} />
                )}
                {selfReview}
              </Fold>
            )}
            {prototypes}
            {proposalFold}
            {evidenceFold}
          </div>
          {personOnlySteps.length > 0 && (
            // The steps only a person can do, said before Approve so the
            // approver knows them; the build is told the same.
            <div className="blk" data-testid="person-only-steps">
              <div className="bh">
                <span className="label">Needs a person</span>
              </div>
              <div className="persons">
                {personOnlySteps.map((flag) => (
                  <span
                    key={flag.step}
                    className="wk-gate-person-step"
                    data-testid="wk-gate-person-step"
                  >
                    {personOnlyStepLine(flag.step, flag.why)}
                    {flag.link ? (
                      <>
                        {" · "}
                        <Link to={flag.link.href}>{flag.link.label}</Link>
                      </>
                    ) : null}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      );
    }
    case "choose_repo":
      // The picker that answers it sits below the body (`RepoChoiceBlock`).
      return (
        <>
          <RepoAskCard
            question={gate?.title ?? "Choose the repository this ticket builds in"}
            why={typeof gate?.body === "string" ? gate.body : null}
            askerName={workerName ?? "The agent"}
          />
          <div className="span">
            {proposalFold}
            {evidenceFold}
          </div>
        </>
      );
    case "ask": {
      const question = gateAsk?.headline ?? gate?.title ?? "The run stopped to ask.";
      const why = gateAsk?.need ?? (typeof gate?.body === "string" ? gate.body : null);
      // The one-attempt lane's setup ask and its stop on our side are answered
      // by the footer's verb; a note rides along only if the person wants one.
      const laneAsk = gate ? laneAskOf(gate) : null;
      const setup = laneAsk?.kind === "run_setup" ? laneAsk.ask : null;
      const beforeWork = setup?.stoppedBeforeWork === true;
      // The repository's own page, where its setup and the Workspace
      // variables are changed: for the setup ask, and for an install that failed.
      const repoId =
        laneAsk?.kind === "run_setup" || laneAsk?.kind === "install_failed"
          ? laneAsk.ask.repoId
          : null;
      const setupLink = repoId
        ? {
            label:
              laneAsk?.kind === "install_failed"
                ? "Open the repository's setup and variables"
                : "Open the repository's setup",
            href: `/services/repositories?repo=${encodeURIComponent(repoId)}`,
          }
        : undefined;
      return (
        <>
          <AskCard
            question={question}
            why={why}
            link={gateAsk?.link ?? setupLink}
            askerName={workerName ?? "The agent"}
            answer={answer}
            onAnswer={setAnswer}
            onAsk={() => askAbout(`On ${key}, help me answer: ${question}`)}
            optional={laneAsk != null}
          />
          {beforeWork && (
            <p className="fact span" data-testid="attempt-before-work">
              {attemptBeforeWorkLine(item.latestRunAttemptNo)}
            </p>
          )}
          {gate && isPausedRunAsk(gate) && (
            <div className="stopbox">
              <div className="h">
                <Stamp>paused</Stamp>Stopped to ask
              </div>
              {gateAsk ? (
                <AskFacts facts={gateAsk.facts} limit={3} />
              ) : (
                <p>{itemStopWords(item)}</p>
              )}
            </div>
          )}
          {fletcherCheck}
          <div className="span">
            {!beforeWork && attemptFold}
            {planFold}
            {prototypes}
          </div>
        </>
      );
    }
    case "working": {
      // The one-attempt lane's one run was started as Wright's build; the
      // item's read of its latest run says whether it is planning, as it
      // names Reeve while it plans.
      const run = liveRunOf(ticket.runs, item.currentRunId) ?? ticket.runs[0] ?? null;
      const purpose = run && run.id === item.latestRunId ? item.latestRunPurpose : null;
      return (
        <>
          {proctorStatus}
          {leftForReview}
          <LiveStream
            workItemId={item.id}
            run={run && purpose ? { ...run, purpose } : run}
            runs={ticket.runs}
            workerName={workerName}
            mode="live"
            stage={pipelineStageOf(item)}
            prNumber={item.prNumber}
            laneSpeakers={laneSpeakers}
          />
          {/* Out of Review (a replan over a pull request), Fletcher's last check still shows. */}
          {inReview ? checkCards : fletcherCheck}
          <div className="span">
            {planFold}
            {prototypes}
          </div>
        </>
      );
    }
    case "stopped_infra":
    case "stopped":
    case "out_of_rounds":
      return (
        <>
          <div className="stopbox span" data-testid="ticket-stopbox">
            <div className="h">
              {worker && (
                <WorkerMark
                  avatar={worker.avatar}
                  name={worker.displayName}
                  slug={worker.slug}
                  size="sm"
                />
              )}
              <Stamp>stopped</Stamp>Why it stopped
            </div>
            <p>{gateAsk?.headline ?? itemStoppedSentence(item)}</p>
            {!gateAsk && <StopOpenFindings item={item} />}
            {gateAsk && <AskFacts facts={gateAsk.facts} limit={4} />}
          </div>
          {gateAsk && (
            <Blk label="What would fix it">
              <AskNeed ask={gateAsk} name={workerName} />
              <AskLink link={gateAsk.link} />
              <AskPrLink prUrl={item.prUrl} prNumber={item.prNumber} />
            </Blk>
          )}
          {gate && resumesApprovedPlan(gate) && (
            <AskCard
              question="Anything the restart should know?"
              askerName={workerName ?? "The agent"}
              answer={answer}
              onAnswer={setAnswer}
              onAsk={() => askAbout(`On ${key}, why did this stop? `)}
              optional
            />
          )}
          {/* What Fletcher's last check found stays in view under the stop. */}
          {fletcherCheck}
          <div className="span">
            {footer.kind === "out_of_rounds" && postedReview && (
              <Fold
                title="The open threads"
                count={
                  threads.length > 0
                    ? threads.filter((thread) => thread.isResolved !== true).length
                    : deliveredFindings.length
                }
              >
                <PostedFindings
                  findings={deliveredFindings}
                  threads={threads}
                  dropped={postedReview.refinementDecisions?.dropped ?? null}
                />
              </Fold>
            )}
            {attemptFold}
            {planFold}
            {prototypes}
          </div>
        </>
      );
    case "merge":
      return (
        <>
          <div className="prbox span">
            <PrLabel item={item} />
            <span className="fact" style={{ fontSize: 11.5 }}>
              opened by YAGNI
            </span>
            <span
              className={`st ${gitHubStateChipTone(ticket.merge?.conflict)}`}
              data-testid="ticket-pr-state"
            >
              {gitHubStateChip(ticket.merge?.github, ticket.merge?.conflict)}
            </span>
          </div>
          {proctorStatus}
          {leftForReview}
          {checkCards}
          <div className="span">
            {planFold}
            {prototypes}
            {proposalFold}
            {evidenceFold}
          </div>
        </>
      );
    case "done":
      return (
        <DoneBody
          item={item}
          ticket={ticket}
          planFold={planFold}
          prototypes={prototypes}
          onOpenItem={onOpenItem}
          fletcher={qaCheck}
        />
      );
    case "generic":
    case "loading":
    case "bespoke":
    default:
      return (
        <>
          {gate && ticket.review.findings.length > 0 && (
            <ReviewFindingsCards state={ticket.review} />
          )}
          {gateAsk ? (
            <Blk label={gateAsk.headline}>
              <AskFacts facts={[...gateAsk.facts, ...prAskFacts]} limit={4 + prAskFacts.length} />
              <AskNeed ask={gateAsk} name={workerName} />
              <AskLink link={gateAsk.link} />
              <AskPrLink prUrl={item.prUrl} prNumber={item.prNumber} />
            </Blk>
          ) : (
            <p className="body-p" style={{ color: "var(--text-primary)" }}>
              {gate?.title ??
                (sentence
                  ? `${PIPELINE_STAGE_LABEL[pipelineStageOf(item)]} · ${sentence}`
                  : PIPELINE_STAGE_LABEL[pipelineStageOf(item)])}
            </p>
          )}
          {proctorStatus}
          {leftForReview}
          {/* What Proctor left on the pull request: in Review, and wherever
              Wright handled everything and it waits on a person. */}
          {inReview || (gate && isReadyForPerson(gate)) ? checkCards : fletcherCheck}
          {doc.page && doc.role !== "plan" && (
            <Blk label={doc.role === "outcome" ? "The result" : "The proposal"}>
              <ProposalBody
                text={doc.page.content ?? ""}
                title={item.title}
                isPage={doc.role === "proposal"}
              />
            </Blk>
          )}
          <div className="span">
            {attemptFold}
            {planFold}
            {prototypes}
          </div>
        </>
      );
  }
}

/**
 * The findings Wright's own review still had open when the build finished,
 * or when its rounds ran out before a pull request (ENG-3028, YAG-1106):
 * where each points, whether it blocked, and one line of what, as the
 * record kept them.
 */
function StopOpenFindings({ item }: { item: WorkItem }) {
  const findings = item.latestRunOpenFindings ?? [];
  if (findings.length === 0) return null;
  const total = Math.max(item.latestRunOpenFindingsCount ?? findings.length, findings.length);
  const more = total - findings.length;
  return (
    <div className="stop-findings" data-testid="ticket-stop-findings">
      <ul>
        {findings.map((finding, index) => {
          const place = stopOpenFindingPlace(finding);
          return (
            <li key={index}>
              {place && <code>{place}</code>}
              {place ? " " : ""}
              {finding.severity && <span className="fact">{finding.severity} </span>}
              {finding.message}
            </li>
          );
        })}
      </ul>
      {more > 0 && <p className="fact">and {more} more</p>}
    </div>
  );
}

/** The word beside a closed ticket's pull request: who merged it, or why it is still open or closed. */
function pullRequestEndWord(
  item: WorkItem,
  discard: WorkItem["discard"] | null,
  activity: TicketState["activity"],
): string {
  if (item.phase === "done") return mergedByYagni(activity) ? "Merged by YAGNI" : "Merged";
  if (!discard || discard.pullRequest.closed) return "Closed";
  return discard.pullRequest.closeRequested ? "Not closed yet" : "Left open";
}

/** The receipt: merged, review, QA, tracker, time, from the records that exist. */
function DoneBody({
  item,
  ticket,
  planFold,
  prototypes,
  onOpenItem,
  fletcher = null,
}: {
  item: WorkItem;
  ticket: TicketState;
  planFold: ReactNode;
  prototypes: ReactNode;
  onOpenItem?: (id: string) => void;
  /** Fletcher's last check of the pull request: a Receipt line and its video. */
  fletcher?: TicketQaCheck | null;
}) {
  const credits = receiptCredits({ item, activity: ticket.activity, gates: ticket.gateHistory });
  const verification = receiptVerification(ticket.gateHistory);
  const checked = fletcherReceiptLine(fletcher);
  const outcome = ticket.doc.role === "outcome" ? ticket.doc.page?.content : null;
  const summary = outcome?.trim() || item.description?.trim() || "";
  const discard = item.phase === "dismissed" ? (item.discard ?? null) : null;
  const withdrawn =
    item.phase === "dismissed" && !discard ? teamWithdrawalReason(ticket.gateHistory) : null;
  return (
    <>
      {withdrawn && (
        <Blk label="Why it was closed">
          <p className="fact" style={{ fontSize: 11.5 }}>
            Withdrawn by the Team
          </p>
          <Markdown className="brief" text={withdrawn} />
        </Blk>
      )}
      {discard && (
        <Blk label={DISCARDED_WHY_HEADING}>
          <p className="fact" style={{ fontSize: 11.5 }}>
            {[discard.discardedBy, discardedFacts(discard)].filter(Boolean).join(" · ")}
          </p>
          {discard.reason?.trim() ? (
            <Markdown className="brief" text={discard.reason.trim()} />
          ) : (
            <p className="fact">{DISCARDED_NO_REASON}</p>
          )}
        </Blk>
      )}
      {item.prUrl && (
        <div className="prbox span">
          <PrLabel item={item} />
          <span className="st">{pullRequestEndWord(item, discard, ticket.activity)}</span>
        </div>
      )}
      {(credits.length > 0 || verification || checked) && (
        <dl className="receipt" data-testid="ticket-receipt">
          {credits.map((credit) => (
            <FragmentRow key={credit.role} term={credit.role} value={credit.line} />
          ))}
          {verification && <FragmentRow term="QA" value={verification} />}
          {checked && <FragmentRow term="checked" value={checked} />}
        </dl>
      )}
      <FletcherReceiptVideo check={fletcher} />
      {summary && (
        <Blk
          label={
            item.phase === "done"
              ? "What changed"
              : // A split is not closed: its tickets carry the work (YAG-1111).
                discard || withdrawn || item.phase === "split"
                ? "The ticket"
                : "Why it was closed"
          }
        >
          <Markdown className="brief" text={summary} />
        </Blk>
      )}
      <div className="span">
        {wasSplit(item) && <SplitChildrenFold item={item} defaultOpen onOpen={onOpenItem} />}
        {planFold}
        {prototypes}
      </div>
    </>
  );
}

function FragmentRow({ term, value }: { term: string; value: string }) {
  return (
    <>
      <dt>{term}</dt>
      <dd>{value}</dd>
    </>
  );
}
